import React, { useCallback, useState } from 'react';
import { ExpandIcon } from 'lucide-react';
import { lookInside } from '../data/content';
import { ImageSlot } from './ImageSlot';
import { Lightbox } from './Lightbox';
import { SectionHeading } from './SectionHeading';
type LookInsideSectionProps = {
  pageImages: string[];
};

/** "See what's inside": real 4:5 pages of the PDF, swipe on mobile, tap to open full size */
export function LookInsideSection({
  pageImages
}: LookInsideSectionProps) {
  const [active, setActive] = useState<number | null>(null);
  const close = useCallback(() => setActive(null), []);

  // Hidden until the real pages of the PDF are loaded in data/images.ts
  if (pageImages.length === 0) return null;
  return <section id="look-inside" aria-labelledby="look-title" className="scroll-mt-4 bg-ink py-12 md:py-20" data-id="element-100" data-mp="1kahnxj2921my">
      <div className="mx-auto max-w-page px-5 md:px-8" data-id="element-101" data-mp="3r060cc71i7y2">
        <SectionHeading id="look-title" tone="dark" eyebrow={lookInside.eyebrow} title={lookInside.title} sub={lookInside.sub} data-id="element-102" data-mp="3m0t5b460luke" />
        <p className="mt-3 text-center text-[13px] text-white/75" data-id="element-103" data-mp="3b58c1dfrxn7b">{lookInside.hint}</p>
      </div>

      <ul className="no-scrollbar mx-auto mt-8 flex max-w-page snap-x snap-mandatory gap-3 overflow-x-auto scroll-px-5 px-5 pb-2 md:mt-10 md:gap-4 md:scroll-px-8 md:px-8" aria-label="Sample pages" data-id="element-104" data-mp="3g4bbmiczj17w">
        {pageImages.map((url, i) => <li key={lookInside.captions[i] ?? i} className="w-[72%] shrink-0 snap-start sm:w-[44%] md:w-[30%] lg:w-[23%]" data-id="element-105" data-mp="3tuwzklhaq1cn" data-mpi={i}>
            <button type="button" onClick={() => setActive(i)} className="group block w-full text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-sun" aria-label={`Open full size: ${lookInside.captions[i]}`} data-id="element-106" data-mp="2nkgu20rlfum">
              <div className="relative aspect-[4/5] overflow-hidden rounded-xl bg-white shadow-[0_18px_40px_-18px_rgba(0,0,0,0.7)]" data-id="element-107" data-mp="29go0jbszlqwl">
                <ImageSlot url={url} alt={lookInside.captions[i]} slotName={`pageImages[${i}]`} fit="contain" data-id="element-108" data-mp="26d8h3e1icx3u" />
                <span className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-sun text-ink transition-transform duration-150 ease-out group-hover:scale-105" data-id="element-109" data-mp="21endr5qylxb6">
                  <ExpandIcon className="h-4 w-4" aria-hidden="true" data-id="element-110" data-mp="2bcl3xysdq46f" />
                </span>
              </div>
              <p className="mt-3 text-[14px] font-medium text-white" data-id="element-111" data-mp="2pbayb6ntdpr3">
                <span className="mr-2 font-display font-extrabold text-sun" data-id="element-112" data-mp="2kczp8y5cfj9x">{String(i + 1).padStart(2, '0')}</span>
                {lookInside.captions[i]}
              </p>
            </button>
          </li>)}
      </ul>
      <p className="mx-auto mt-4 max-w-page px-5 text-center text-[13px] text-white/70 md:px-8" data-id="element-113" data-mp="2u2j7c1l8f93q">{lookInside.note}</p>

      <Lightbox images={pageImages} captions={lookInside.captions} index={active} onClose={close} onChange={setActive} data-id="element-114" data-mp="383diut3ktq6m" />
    </section>;
}